import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { path } from 'ramda';

import {
  DataBlocks,
  ErrorBlock,
  I18n,
  Tooltip,
  TextInput,
  Status,
  Button,
  Link,
} from 'components';

import ChangePassword from '../ChangePassword';
import { LOGIN_ALREADY_EXISTS_ERROR } from '../../constants';
import styles from './styles.pcss';

const cx = classNames.bind(styles);

const MAX_LOGIN_LENGTH = 20;
const MIN_LOGIN_LENGTH = 5;

const validateNewLogin = (value) => {
  if (!value) {
    return null;
  }
  if (value.length < MIN_LOGIN_LENGTH) {
    return 'too_short';
  }
  if (!/[a-z]/.test(value[0])) {
    return 'first_symbol_only_letter';
  }
  if (!/^[a-z\d]*$/.test(value)) {
    return 'restricted_symbols';
  }
  return null;
};

class InternetAccess extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      newLoginValidationError: null,
    };
  }

  updateNewLogin = (value) => {
    const error = validateNewLogin(value);

    this.setState({
      newLogin: value,
      newLoginValidationError: error,
    });
  };

  isChangeLoginButtonDisabled = () => !this.state.newLogin
                                      || !!this.state.newLoginValidationError
                                      || path(['isUpdating'], this.props.loginUpdatingStatus);

  submitLogin = () => this.props.onSubmitLogin(this.state.newLogin);

  render() {
    const {
      error,
      refreshAction,
      userInternetLogin,
      userLogin,
      canChangeLogin = true,
      loginUpdatingStatus,
      fttbConfigureConnectionHelpUrl,
      passwordPolicy,
    } = this.props;
    const {
      newLogin,
      newLoginValidationError,
    } = this.state;

    if (error) {
      return (
        <ErrorBlock
          title={<I18n path="settings.passwordAndAccess.internetAccesssTitle" />}
          refreshAction={refreshAction}
        />
      );
    }

    return (
      <DataBlocks.Block
        title={<I18n path="settings.passwordAndAccess.internetAccesssTitle" />}
        isLoading={!userInternetLogin}
      >
        <div className={cx('container')}>
          <I18n
            className={cx('label')}
            path="settings.passwordAndAccess.login"
          />
          <div className={cx('inputWrapper')}>
            {userInternetLogin}
          </div>
        </div>

        {canChangeLogin && (
          <>
            {!path(['success'], loginUpdatingStatus) && (
              <>
                <div className={cx('container')}>
                  <div className={cx('label')}>
                    <I18n path="settings.passwordAndAccess.newLogin" />
                    <Tooltip>
                      <I18n path="settings.passwordAndAccess.loginHint" />
                    </Tooltip>
                  </div>
                  <div className={cx('inputWrapper', { fail: Boolean(newLoginValidationError) })}>
                    <TextInput
                      value={newLogin}
                      onChange={this.updateNewLogin}
                      maxLength={MAX_LOGIN_LENGTH}
                    />
                  </div>
                </div>
                {path(['error'], loginUpdatingStatus) === LOGIN_ALREADY_EXISTS_ERROR && (
                  <div className={cx('container')}>
                    <div className={cx('label')} />
                    <div className={cx('statusWrapper')}>
                      <Status className="error" emoji="statusFail">
                        <I18n path="settings.passwordAndAccess.loginAlreadyExistsError" />
                      </Status>
                    </div>
                  </div>
                )}
                <div className={cx('container')}>
                  <div className={cx('label')} />
                  <div className={cx('statusWrapper')}>
                    <Status className="attention" emoji="statusFail">
                      <I18n path="settings.passwordAndAccess.canChangeLoginOnlyOnceAttention" />
                    </Status>
                  </div>
                </div>
                <Button
                  disabled={this.isChangeLoginButtonDisabled()}
                  onClick={this.submitLogin}
                >
                  <I18n path="settings.passwordAndAccess.changeLogin" />
                </Button>
              </>
            )}
            {path(['success'], loginUpdatingStatus)
              && (
              <div className={cx('container')}>
                <div className={cx('label')} />
                <div className={cx('statusWrapper')}>
                  <Status className="success">
                    <I18n
                      path="settings.passwordAndAccess.changeInternetLoginSuccessPart1"
                      vars={{
                        new_login: newLogin,
                        old_login: userInternetLogin,
                      }}
                    />
                    <Link href={fttbConfigureConnectionHelpUrl}>
                      <I18n path="settings.passwordAndAccess.changeInternetLoginSuccessPart2" />
                    </Link>
                    <I18n path="settings.passwordAndAccess.changeInternetLoginSuccessPart3" />
                  </Status>
                </div>
              </div>
              )}
          </>
        )}

        <ChangePassword
          onSubmitPassword={this.props.onSubmitPassword}
          passwordUpdatingStatus={this.props.passwordUpdatingStatus}
          rulesHint={passwordPolicy.text}
          regexp={passwordPolicy.regex}
          userInternetLogin={userInternetLogin}
          userLogin={userLogin}
          isFttb
        />
      </DataBlocks.Block>
    );
  }
}

InternetAccess.propTypes = {
  error: PropTypes.string,
  refreshAction: PropTypes.func,
  onSubmitLogin: PropTypes.func,
  onSubmitPassword: PropTypes.func,
  userInternetLogin: PropTypes.string,
  userLogin: PropTypes.string,
  passwordUpdatingStatus: PropTypes.object,
  loginUpdatingStatus: PropTypes.object,
  canChangeLogin: PropTypes.bool,
  fttbConfigureConnectionHelpUrl: PropTypes.string,
  passwordPolicy: PropTypes.object,
};

export default InternetAccess;
